/**
 * Who has been using the desk, and what they were last doing.
 *
 * TWO SIGNALS BEHIND ONE LIST — a ticket heartbeat that knows exactly which
 * ticket, and a page view that covers every other screen. The server merges
 * them; this only has to show which it was, because the precision differs
 * and a reader should be able to tell.
 *
 * WHAT IT DOES NOT SHOW, deliberately: how long anybody has been anywhere.
 * "On this ticket for 40 minutes" is a claim an idle tab makes on somebody's
 * behalf, and a list of everyone with durations beside their names stops
 * being an operational view and becomes a timesheet nobody agreed to. A last
 * signal is a fact; a duration is an inference about a person.
 */
import { useQuery } from '@tanstack/react-query';
import { Circle } from 'lucide-react';
import { api } from '../../lib/api.js';
import { Avatar, Panel, Spinner } from '../../ui/index.js';

export interface ActivePerson {
  userOid: string;
  name: string | null;
  email: string;
  doing: string;
  ticketNumber: number | null;
  secondsAgo: number;
  via: 'ticket' | 'page';
}

/** Live enough that the person is almost certainly at the keyboard. */
const LIVE_SECONDS = 120;

/**
 * "just now", "4m", "2h" — coarse on purpose.
 *
 * The exact second is noise in a list somebody is scanning, and rounding up
 * is the honest direction: a signal 119 seconds old is better described as
 * "2m" than as "1m".
 */
export function ago(seconds: number): string {
  if (seconds < 45) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${Math.max(1, minutes)}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function WhoIsHere({ tenant }: { tenant: string }) {
  const q = useQuery({
    queryKey: ['active-people', tenant],
    queryFn: () => api.get<{ people: ActivePerson[] }>(`/api/v1/tenants/${tenant}/admin/active`),
    /**
     * The one panel here that IS worth polling. Everything else on this
     * screen is a period summary that does not change while you look at it;
     * this one is the answer to "who is on right now", which is wrong the
     * moment it is stale.
     */
    refetchInterval: 30_000,
  });

  /**
   * ONE ENTRY PER PERSON, whatever the server sends. The query once returned
   * a row per role and per open tab, and the list is keyed by person: React
   * cannot reconcile duplicate keys, so every 30-second poll left stale rows
   * behind and one person piled up five and six times over. The rows arrive
   * freshest first, so the first one kept is their latest.
   */
  const seen = new Set<string>();
  const people = (q.data?.people ?? []).filter((p) => !seen.has(p.userOid) && !!seen.add(p.userOid));
  const live = people.filter((p) => p.secondsAgo <= LIVE_SECONDS).length;

  return (
    <Panel
      title="Who is here"
      subtitle={
        q.isLoading
          ? undefined
          : live > 0
            ? `${live} active in the last couple of minutes`
            : 'nobody in the last couple of minutes'
      }
    >
      {q.isLoading ? (
        <Spinner />
      ) : people.length === 0 ? (
        <p className="text-xs text-ink-tertiary">
          Nobody has used this group in the last day.
        </p>
      ) : (
        /* BOUNDED, whatever the headcount.
           A desk of fifteen people made this one list fifteen rows tall and
           the dashboard under it disappeared. Two columns on anything wider
           than a phone halves that, and the scroll caps it: the panel is a
           fixed piece of furniture rather than something that grows with the
           team. */
        <ul className="max-h-56 overflow-y-auto sm:grid sm:grid-cols-2 sm:gap-x-6">
          {people.map((p) => {
            const isLive = p.secondsAgo <= LIVE_SECONDS;
            return (
              <li
                key={p.userOid}
                className="flex items-center gap-3 border-b border-line-subtle py-1.5 last:border-0"
              >
                <Avatar name={p.name} email={p.email} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-ink">
                    {p.name ?? p.email}
                  </p>
                  {/* Only when it adds something. Somebody the directory has
                      no name for was showing their address on both lines,
                      which reads as a rendering fault rather than as a
                      missing name. */}
                  {p.name !== null && (
                    <p className="truncate text-2xs text-ink-tertiary">{p.email}</p>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-xs text-ink-secondary">
                    {/* A dot only while it is actually live — a green dot
                        against a two-hour-old signal is a lie told in
                        colour. */}
                    {isLive && (
                      <Circle
                        aria-hidden
                        className="mr-1.5 inline h-2 w-2 fill-ok text-ok"
                      />
                    )}
                    {p.doing}
                  </p>
                  <p className="text-2xs text-ink-tertiary">
                    {ago(p.secondsAgo)}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
